// First-party funnel events -> POST /funnel_events (FunnelEventsController).
//
// Visitor id, A/B variants and UTM/referrer touches live in bs_* cookies set by
// the inline script in layouts/_funnel_head.html.erb; the beacon carries them
// as cookies, so this file only says what happened. Works on Cloudflare-cached
// pages: nothing here depends on server-rendered per-visitor HTML.
//
// Markup-only API (one delegated listener; add attributes, no JS):
//   data-track="buy_click"                 click on this element sends buy_click
//   data-track-product="123"               product id for any tracked element
//   data-track-prop-plan="yearly"          extra property (plan: "yearly"), up to 8
//   data-track-view="product_page_view"    sent once when the element renders
//   data-track-cta="hero"                  cta_click (place: "hero") on click, on top
//                                          of the automatic events below
//   data-track-section="faq"               section_view (section: "faq") once, when
//                                          the element first scrolls into view
//   data-track-upsell="lifetime_strip"     upsell_shown on render, upsell_clicked
//                                          on any link/button click inside
//                                          (hidden on load? call trackShown(el)
//                                          when you reveal it)
// Automatic, no attributes needed:
//   links to /tools, /templates, /pricing, /lifetime, /bigyear -> offer_link_click
//   links to membership checkout (/subscriptions/new, sign-up with a plan) -> buy_click

const ENDPOINT = '/funnel_events';
const OFFER_PATH = /^\/(tools|templates|pricing|lifetime|bigyear)(\/|$)/;
const CHECKOUT_PATH = /^\/subscriptions\/new\/?$/;
const SIGNUP_PATH = /^\/users\/sign_up\/?$/;
const seen = new WeakSet();
// Landing-page funnel events, also sent to GA4 under the same names.
const GA4_MIRROR = new Set(['cta_click', 'section_view']);

function enabled() {
  return document.cookie.indexOf('bs_vid=') !== -1 && !location.pathname.startsWith('/admin');
}

function snake(key) {
  return key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`).replace(/^_/, '');
}

function propsFrom(el) {
  const props = {};
  if (!el || !el.dataset) return props;
  Object.keys(el.dataset).forEach((key) => {
    if (key.startsWith('trackProp') && key.length > 9) props[snake(key.slice(9))] = el.dataset[key];
  });
  return props;
}

function send(name, data = {}) {
  if (GA4_MIRROR.has(name) && typeof window.gtag === 'function') {
    try {
      window.gtag('event', name, { page_path: location.pathname, ...(data.props || {}) });
    } catch {
      // tracking must never break the page
    }
  }
  if (!enabled()) return;
  const payload = { name, path: location.pathname };
  if (data.product) payload.product_id = String(data.product);
  if (data.target) payload.target = data.target;
  if (data.props && Object.keys(data.props).length) payload.props = data.props;
  const body = JSON.stringify(payload);

  try {
    // text/plain keeps it a CORS-simple request; the beacon includes cookies.
    if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))) return;
  } catch {
    // fall through to fetch
  }
  try {
    fetch(ENDPOINT, {
      method: 'POST',
      body,
      keepalive: true,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'text/plain' },
    }).catch(() => {});
  } catch {
    // tracking must never break the page
  }
}

// Same-origin destination of a link or submit button, as a URL (or null).
function destination(el) {
  let href = null;
  if (el.tagName === 'A') href = el.getAttribute('href');
  else if (el.form) href = el.getAttribute('formaction') || el.form.getAttribute('action');
  if (!href || href.startsWith('#') || /^(mailto|tel|javascript):/i.test(href)) return null;
  try {
    const url = new URL(href, location.href);
    return url.origin === location.origin ? url : null;
  } catch {
    return null;
  }
}

function membershipPlan(url) {
  if (CHECKOUT_PATH.test(url.pathname)) return url.searchParams.get('product') || 'membership';
  if (SIGNUP_PATH.test(url.pathname)) return url.searchParams.get('target');
  return null;
}

function onClick(event) {
  const el = event.target.closest && event.target.closest('a[href], button, input[type="submit"], [data-track]');
  if (!el) return;

  const url = destination(el);
  const target = url ? url.pathname : undefined;
  const tracked = el.closest('[data-track]');

  if (tracked) {
    send(tracked.dataset.track, { product: tracked.dataset.trackProduct, target, props: propsFrom(tracked) });
  } else if (url && membershipPlan(url)) {
    send('buy_click', { target, props: { plan: membershipPlan(url), ...propsFrom(el) } });
  } else if (url && el.tagName === 'A' && OFFER_PATH.test(url.pathname) && url.pathname !== location.pathname) {
    send('offer_link_click', { target, product: el.dataset.trackProduct, props: propsFrom(el) });
  }

  const cta = el.closest('[data-track-cta]');
  if (cta) send('cta_click', { target, props: { place: cta.dataset.trackCta, ...propsFrom(cta) } });

  const upsell = el.closest('[data-track-upsell]');
  if (upsell && el.matches('a[href], button, input[type="submit"]')) {
    send('upsell_clicked', { target, props: { upsell: upsell.dataset.trackUpsell, ...propsFrom(upsell) } });
  }
}

// Sends the view / upsell_shown events for one marked element, once per page.
function markShown(el) {
  if (seen.has(el)) return;
  // display:none copies (mobile/desktop duplicates, inactive A/B variants)
  // were never shown; skip them so upsell_shown counts what people saw.
  if (el.getClientRects().length === 0) return;
  seen.add(el);
  if (el.dataset.trackView) {
    send(el.dataset.trackView, { product: el.dataset.trackProduct, props: propsFrom(el) });
  }
  if (el.dataset.trackUpsell) {
    send('upsell_shown', { props: { upsell: el.dataset.trackUpsell, ...propsFrom(el) } });
  }
}

let sectionObserver = null;

function watchSections(root) {
  const sections = root.querySelectorAll('[data-track-section]');
  if (!sections.length || typeof IntersectionObserver !== 'function') return;
  sectionObserver ||= new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      sectionObserver.unobserve(entry.target);
      if (seen.has(entry.target)) return;
      seen.add(entry.target);
      send('section_view', { props: { section: entry.target.dataset.trackSection } });
    });
  // Counted once its top passes 70% of the screen: a ratio threshold would never
  // fire for a section taller than the screen.
  }, { rootMargin: '0px 0px -30% 0px' });
  sections.forEach((el) => { if (!seen.has(el)) sectionObserver.observe(el); });
}

function scan(root) {
  if (!root || !root.querySelectorAll) return;
  root.querySelectorAll('[data-track-view], [data-track-upsell]').forEach(markShown);
  watchSections(root);
}

// For markup that starts hidden and is revealed later (e.g. a <dialog> opened
// by a controller): the load-time scan skipped it, so call this once it is
// visible. Safe to call repeatedly; each element is counted once per page.
export function trackShown(el) {
  if (!el || !el.dataset || !(el.dataset.trackView || el.dataset.trackUpsell)) return;
  markShown(el);
}

let started = false;

export function startFunnelTracking() {
  if (started) return;
  started = true;
  document.addEventListener('click', onClick, true);
  document.addEventListener('turbo:load', () => scan(document.body));
  document.addEventListener('turbo:frame-load', (event) => scan(event.target));
  // Turbo Stream swaps (e.g. the member area after an in-page free unlock) fire no
  // load event; wrap the stream's render and scan once the new markup is in.
  document.addEventListener('turbo:before-stream-render', (event) => {
    const render = event.detail.render;
    event.detail.render = async (stream) => {
      await render(stream);
      scan(document.body);
    };
  });
  if (document.readyState !== 'loading') scan(document.body);
}
